<script setup lang="js">
import { ref, onMounted, computed, watch } from 'vue';
import Multiselect from 'vue-multiselect';

import { MODAL_SIDE } from '@/const';

import { useModalStore } from '@/stores/modal.store';
import ModalTemplate from '@/components/newModals/ModalTemplate.vue';

const props = defineProps({
  outputEntity: { type: Object },
  customVariables: {
    type: Array,
    required: true,
  },
  availableOutputEntities: {
    type: Array,
    required: true,
  }
});

const modalStore = useModalStore();

const variable = ref(null);
const entity = ref(null);
const columns = ref(null);
const searchQueries = ref({});

const transformedEntities = computed(() =>
  props.availableOutputEntities
    .map((ent) => {
      const result = { ...ent };
      const match = ent.name.match(/^(?<entity>(?:input_.+)|(?:table_column))\.(?<id>\d+)$/);

      if (match) {
        result.entity = match.groups.entity;
        result.id = match.groups.id;
      }

      return result;
    })
    .filter((ent) => ent.type === variable.value?.type)
);

function saveData() {
  modalStore.modalEmit('save', {
    entity: entity.value.entity,
    entity_id: entity.value.id,
    variable: variable.value.name,
    columns: columns.value
  });
}

function updateByProps(output) {
  if (!output) return;

  columns.value = output.columns;
  variable.value = props.customVariables.find((v) => v.name === output.variable);
  entity.value = transformedEntities.value.find(
    (ent) => ent.entity === output.entity && ent.id === output.entity_id
  );
}

watch(() => props.outputEntity, (value) => updateByProps(value));

onMounted(() => updateByProps(props.outputEntity));
</script>

<template>
  <ModalTemplate :variant="MODAL_SIDE" id="output-modal">
    <template #title>
      <span v-if="props.outputEntity">Редактировать выходные данные</span>
      <span v-else>Добавить выходные данные</span>
    </template>
    <template #default>
      <div class="mt-3">
        <label class="form-label">Переменная</label>
        <Multiselect
          v-model="variable"
          :options="props.customVariables"
          placeholder="Поиск..."
          :multiple="false"
          label="title"
          track-by="name"
          class="multiselect-style"
        />
      </div>
      <div class="mt-3">
        <label class="form-label">Поле (в исполняемой системе)</label>
        <Multiselect
          v-model="entity"
          :options="transformedEntities"
          placeholder="Поиск..."
          :multiple="false"
          label="title"
          track-by="id"
          class="multiselect-style"
        />
      </div>

      <div
        class="mt-4"
        v-if="entity && entity.entity === 'input_table'"
      >
        <h5>
          Столбцы (выберите соответствующие значения из переменной массива для записи в столбцы
          таблицы)
        </h5>
        <div class="mt-3" v-for="col in entity.columns" :key="col.name">
          <label class="form-label">{{ col.title }}</label>
          <input
            type="text"
            v-model="searchQueries[col.name]"
            placeholder="Поиск..."
            class="form-control mb-2"
          />
          <select v-model="columns[col.name]" class="form-select">
            <template
              v-for="arrCol in filteredOptions(variable.table_columns, col.name)"
              :key="arrCol.name"
            >
              <option :value="arrCol.name" v-if="arrCol.type === col.type">
                {{ arrCol.title }}
              </option>
            </template>
          </select>
        </div>
      </div>
    </template>
    <template #footer="{ close }">
      <button class="btn-default btn-cancel" @click="close">Отмена</button>
      <button class="btn-default btn-primary" @click="saveData">Сохранить</button>
    </template>
  </ModalTemplate>
</template>
